import * as Yup from "yup";

// Personal Information Validation Schema
export const personalInfoSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  confirmEmail: Yup.string()
    .email("Please enter a valid email address")
    .oneOf([Yup.ref("email")], "Email addresses must match")
    .required("Please confirm your email"),
  city: Yup.string().max(100, "City name must not exceed 100 characters"),
  phoneNumber: Yup.string()
    .matches(/^\d+$/, "Phone number must contain only numbers")
    .required("Phone number is required"),
  countryCode: Yup.string().required("Country code is required"),
  profileImage: Yup.mixed()
    .required("User image is required")
    .test("fileType", "Only image files are allowed", (value: any) => {
      if (!value) return false;
      return typeof value.type === "string" && value.type.startsWith("image/");
    })
    .test("fileSize", "Image must be less than 2MB", (value: any) => {
      if (!value) return false;
      return value.size <= 2 * 1024 * 1024;
    }),
  cvFile: Yup.mixed()
    .required("User CV is required")
    .test("fileSize", "File size too large", (value: any) => {
      if (!value) return false;
      return value.size <= 2 * 1024 * 1024; // 2MB
    })
    .test("fileType", "Unsupported file format", (value: any) => {
      if (!value) return false;
      return [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(value.type);
    }),
});

const dateValidator = (message: string) =>
  Yup.date()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Please enter a valid date")
    .max(new Date(), `${message} cannot be in the future`);

// Allow future dates, used for end dates that can be in future
const flexibleDateValidator = () =>
  Yup.date()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Please enter a valid date");

export const experienceSchema = Yup.object().shape({
  title: Yup.string().required("Job title is required"),
  company: Yup.string().required("Company name is required"),
  startDate: dateValidator("Start date").required("Start date is required"),
  endDate: flexibleDateValidator().when("currentlyWorking", {
    is: false,
    then: (schema) =>
      schema
        .required("End date is required")
        .test("after-start", "Invalid date", function (value) {
          const start = this.parent.startDate as Date | string | null;
          if (!value || !start) return false;
          const startDate = new Date(start as any);
          const endDate = new Date(value as any);
          return endDate > startDate; // strictly after
        }),
    otherwise: (schema) => schema.nullable(),
  }),
  currentlyWorking: Yup.boolean(),
});

export const educationSchema = Yup.object().shape({
  institution: Yup.string().required("Institution name is required"),
  startDate: Yup.date()
    .transform((value, originalValue) => {
      // Transform empty string to null for proper validation
      return originalValue === "" ? null : value;
    })
    .typeError("Please enter a valid date")
    .required("Start date is required")
    .max(new Date(), "Start date cannot be in the future")
    .test("valid-date", "Please enter a valid start date", function (value) {
      if (!value) return false;
      return !isNaN(value.getTime());
    }),
  endDate: Yup.date()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    })
    .typeError("Please enter a valid date")
    .when("currentlyAttending", {
      is: true,
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema
          .required("End date is required")
          .test("after-start", "Invalid date", function (value) {
            const start = this.parent.startDate as Date | string | null;
            if (!value || !start) return false;
            const startDate = new Date(start as any);
            const endDate = new Date(value as any);
            return endDate > startDate; // strictly after
          })
          .test(
            "valid-date",
            "Please enter a valid end date",
            function (value) {
              if (!value) return false;
              return !isNaN(value.getTime());
            }
          ),
    }),
  currentlyAttending: Yup.boolean(),
});

// Main Form Validation Schema
export const mainFormValidationSchema = Yup.object().shape({
  personalInfo: personalInfoSchema,
  experience: Yup.array().of(experienceSchema),
  // .min(1, "At least one experience entry is required"),
  education: Yup.array().of(educationSchema),
  // .min(1, "At least one education entry is required"),

  dataProcessingConsent: Yup.boolean().oneOf(
    [true],
    "You must accept the privacy policy before submitting"
  ),
});
